/*
 * Kept in step with the backend's src/services/media/adtsAudio.ts (transcripts
 * read the same recordings on the server); a fix in one belongs in the other.
 */
/**
 * Raw AAC recordings (.aac, ADTS): AAC frames each with a small header that
 * gives its length. Like an MP3 there is no index, so the frames are walked
 * from the start (or from the nearest entry of a seek index made by one
 * earlier walk of the whole file) to the stretch asked for.
 *
 * The result is an ordinary AAC AudioTrack whose packets are the frames'
 * raw payloads, so everything that reads AAC out of an MP4 (which rebuilds
 * ADTS headers) reads these the same way. Every frame holds 1024 samples per
 * raw data block; times run from the first frame, as players show the file.
 */

import type { ByteSource } from './byteSource';
import { AacCodec, AudioTrack, MediaReadError, PacketTable } from './isoAudio';

export interface AdtsSeekIndex {
  /** Frames between entries. */
  every: number;
  /** Byte offset of frame i · every. */
  offsets: number[];
  /** Frames counted. */
  frames: number;
}

export interface AdtsLayout {
  /** First frame (after any ID3 tag). */
  audioStart: number;
  dataEnd: number;
  codec: AacCodec;
  /** Frames in the file, once counted; null until then. */
  frameCount: number | null;
  /** Bytes per frame over the first frames, for estimating the length before a walk. */
  averageFrameBytes: number;
}

export interface AdtsAudioTrack extends AudioTrack {
  adts: AdtsLayout;
  index: AdtsSeekIndex | null;
}

export interface AdtsReadOptions {
  signal?: AbortSignal;
  fromS?: number;
  toS?: number;
  layout?: AdtsLayout;
  index?: AdtsSeekIndex | null;
  /** Walk the whole file to count its frames and build a seek index. */
  buildIndex?: boolean;
  blockBytes?: number;
  /** Refuse (MediaReadError) rather than walk more than this many bytes. */
  maxScanBytes?: number;
}

const SAMPLE_RATES = [96000, 88200, 64000, 48000, 44100, 32000, 24000, 22050, 16000, 12000, 11025, 8000, 7350];
const SAMPLES_PER_BLOCK = 1024;
const INDEX_EVERY = 1000;
/** Frames read before the stretch asked for (the decoder settles on them). */
const LEAD_IN_FRAMES = 2;

interface AdtsHeader {
  headerBytes: number;
  frameBytes: number;
  blocks: number;
  objectType: number;
  samplingIndex: number;
  channelConfig: number;
}

/** The ADTS header at `at`, or null. */
export const parseAdtsHeader = (data: Uint8Array, at: number): AdtsHeader | null => {
  if (at < 0 || at + 7 > data.length) return null;
  if (data[at] !== 0xff || (data[at + 1] & 0xf6) !== 0xf0) return null; // sync, layer 0
  const samplingIndex = (data[at + 2] >> 2) & 0x0f;
  if (samplingIndex >= SAMPLE_RATES.length) return null;
  const frameBytes = ((data[at + 3] & 0x03) << 11) | (data[at + 4] << 3) | (data[at + 5] >> 5);
  const headerBytes = (data[at + 1] & 0x01) === 1 ? 7 : 9;
  if (frameBytes <= headerBytes) return null;
  return {
    headerBytes,
    frameBytes,
    blocks: (data[at + 6] & 0x03) + 1,
    objectType: (data[at + 2] >> 6) + 1,
    samplingIndex,
    channelConfig: ((data[at + 2] & 0x01) << 2) | (data[at + 3] >> 6),
  };
};

/** True for the first bytes of an ADTS stream. */
export const isAdtsHeader = (head: Uint8Array): boolean => parseAdtsHeader(head, 0) !== null;

/** Reads a file forwards in blocks, keeping the current one. */
class BlockReader {
  private block: Uint8Array = new Uint8Array(0);
  private start = 0;
  fetched = 0;

  constructor(private readonly source: ByteSource, private readonly blockBytes: number, private readonly signal?: AbortSignal) {}

  async at(offset: number, length: number): Promise<[Uint8Array, number]> {
    if (offset < this.start || offset + length > this.start + this.block.length) {
      const wanted = Math.min(Math.max(length, this.blockBytes), Math.max(0, this.source.size - offset));
      this.block = wanted > 0 ? await this.source.read(offset, wanted, this.signal) : new Uint8Array(0);
      this.start = offset;
      this.fetched += this.block.length;
    }
    return [this.block, offset - this.start];
  }
}

const id3End = async (reader: BlockReader, dataEnd: number): Promise<number> => {
  let offset = 0;
  for (let tags = 0; tags < 8 && offset + 10 <= dataEnd; tags += 1) {
    const [block, at] = await reader.at(offset, 10);
    if (block[at] !== 0x49 || block[at + 1] !== 0x44 || block[at + 2] !== 0x33) break;
    const size = ((block[at + 6] & 0x7f) << 21) | ((block[at + 7] & 0x7f) << 14) | ((block[at + 8] & 0x7f) << 7) | (block[at + 9] & 0x7f);
    offset += 10 + size + ((block[at + 5] & 0x10) !== 0 ? 10 : 0);
  }
  return offset;
};

/** True when the bytes after any ID3 tag are an ADTS stream. */
export const startsWithAdtsAfterId3 = async (source: ByteSource, signal?: AbortSignal): Promise<boolean> => {
  const reader = new BlockReader(source, 64 * 1024, signal);
  const start = await id3End(reader, source.size);
  if (start + 7 > source.size) return false;
  const [block, at] = await reader.at(start, 7);
  return parseAdtsHeader(block, at) !== null;
};

/** Reads an ADTS file's layout: where its frames start and what they hold. */
export const readAdtsLayout = async (source: ByteSource, signal?: AbortSignal): Promise<AdtsLayout> => {
  const reader = new BlockReader(source, 64 * 1024, signal);
  let dataEnd = source.size;
  if (dataEnd >= 128) {
    const [tail, at] = await reader.at(dataEnd - 128, 128);
    if (tail[at] === 0x54 && tail[at + 1] === 0x41 && tail[at + 2] === 0x47) dataEnd -= 128; // ID3v1
  }
  const audioStart = await id3End(reader, dataEnd);
  const [block, at] = await reader.at(audioStart, 7);
  const first = parseAdtsHeader(block, at);
  if (!first) throw new MediaReadError('not-mp4', 'This file is not an AAC stream.');
  // A few frames give the average size (for an estimated length) and check the stream holds together.
  let position = audioStart;
  let frames = 0;
  for (; frames < 32 && position + 7 <= dataEnd; frames += 1) {
    const [data, index] = await reader.at(position, 7);
    const header = parseAdtsHeader(data, index);
    if (!header || header.samplingIndex !== first.samplingIndex) break;
    position += header.frameBytes;
  }
  if (frames < 2 && position < dataEnd) throw new MediaReadError('corrupt', 'The AAC stream could not be read.');
  const sampleRate = SAMPLE_RATES[first.samplingIndex];
  return {
    audioStart,
    dataEnd,
    codec: {
      kind: 'aac',
      objectType: first.objectType,
      samplingIndex: first.samplingIndex,
      channelConfig: first.channelConfig,
      sampleRate,
      channels: first.channelConfig === 7 ? 8 : Math.max(1, first.channelConfig),
    },
    frameCount: position >= dataEnd ? frames : null,
    averageFrameBytes: Math.max(1, (position - audioStart) / Math.max(1, frames)),
  };
};

/**
 * The frames covering presentation seconds [fromS, toS] (and a couple
 * before), as raw AAC payloads with exact times. With no seek index the walk
 * starts at the first frame; `buildIndex` walks the whole file and returns
 * an index for next time.
 */
export const readAdtsTrack = async (source: ByteSource, options: AdtsReadOptions = {}): Promise<AdtsAudioTrack> => {
  const { signal } = options;
  const layout = options.layout ?? (await readAdtsLayout(source, signal));
  const rate = layout.codec.sampleRate;
  const fromS = options.fromS ?? 0;
  const toS = options.toS ?? Infinity;
  const reader = new BlockReader(source, options.blockBytes ?? 1024 * 1024, signal);
  const index = options.index && options.index.offsets.length > 0 ? options.index : null;
  const countAll = Boolean(options.buildIndex) && !index;
  const maxScan = options.maxScanBytes ?? Infinity;
  // Blocks per frame are nearly always 1; times count blocks as they come.
  const firstWanted = Math.max(0, Math.floor((fromS * rate) / SAMPLES_PER_BLOCK) - LEAD_IN_FRAMES);
  const endWanted = Number.isFinite(toS) ? Math.ceil((toS * rate) / SAMPLES_PER_BLOCK) + 2 : Infinity;

  let frame = 0;
  let blocksBefore = 0;
  let offset = layout.audioStart;
  if (!countAll && index && firstWanted > 0) {
    const entry = Math.min(Math.floor(firstWanted / index.every), index.offsets.length - 1);
    const [data, at] = await reader.at(index.offsets[entry], 7);
    if (parseAdtsHeader(data, at)) {
      frame = entry * index.every;
      blocksBefore = frame;
      offset = index.offsets[entry];
    }
  }
  const times: number[] = [];
  const offsets: number[] = [];
  const sizes: number[] = [];
  const durations: number[] = [];
  const indexOffsets: number[] = [];
  const fetchedBefore = reader.fetched;
  while (offset + 7 <= layout.dataEnd) {
    if (!countAll && frame >= endWanted) break;
    if (reader.fetched - fetchedBefore > maxScan) {
      throw new MediaReadError('unsupported-audio', 'This AAC file would have to be read in full first.');
    }
    const [data, at] = await reader.at(offset, 9);
    const header = parseAdtsHeader(data, at);
    if (!header || header.samplingIndex !== layout.codec.samplingIndex || offset + header.frameBytes > layout.dataEnd) break;
    if (countAll && frame % INDEX_EVERY === 0) indexOffsets.push(offset);
    if (frame >= firstWanted && frame < endWanted) {
      times.push(blocksBefore * SAMPLES_PER_BLOCK);
      durations.push(header.blocks * SAMPLES_PER_BLOCK);
      offsets.push(offset + header.headerBytes);
      sizes.push(header.frameBytes - header.headerBytes);
    }
    frame += 1;
    blocksBefore += header.blocks;
    offset += header.frameBytes;
  }
  const builtIndex = indexOffsets.length > 0 ? { every: INDEX_EVERY, offsets: indexOffsets, frames: frame } : null;
  const frameCount = builtIndex ? builtIndex.frames : layout.frameCount ?? index?.frames ?? null;
  const resolved: AdtsLayout = frameCount !== layout.frameCount ? { ...layout, frameCount } : layout;
  const durationSeconds = frameCount !== null
    ? (frameCount * SAMPLES_PER_BLOCK) / rate
    : ((layout.dataEnd - layout.audioStart) / layout.averageFrameBytes) * (SAMPLES_PER_BLOCK / rate);
  const table: PacketTable = {
    kind: 'packets',
    count: times.length,
    times: Float64Array.from(times),
    durations: Float64Array.from(durations),
    offsets: Float64Array.from(offsets),
    sizes: Uint32Array.from(sizes),
  };
  return {
    codec: layout.codec,
    timescale: rate,
    mediaStart: 0,
    startDelay: 0,
    table,
    fragmented: false,
    durationSeconds,
    complete: true,
    adts: resolved,
    index: builtIndex ?? index,
  };
};

export const isAdtsTrack = (track: AudioTrack): track is AdtsAudioTrack => Boolean((track as AdtsAudioTrack).adts);
